"use client";

import { useState, useEffect } from "react";
import { useToast } from "../components/context/ToastContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IHistory, EnumStatusConsultation } from "../interfaces/history";

import {
    concluirConsultaCpf,
    fetchCpfHistory,
    alterarStatusCpf,
    fetchCpfStatusChanges,
    IStatusChangeLog,
} from "../services/historyCpfService";

export function useCpfHistory(initialPage: number = 1) {
    const showToast = useToast();
    const queryClient = useQueryClient();

    const [search, setSearch] = useState("");
    const [pageNumber, setPageNumber] = useState(initialPage);

    const [filters, setFilters] = useState({
        protocol: "",
        document: "",
        startDate: "",
        endDate: "",
        status: "",
        deviceId: "",
        geoLocalizacao: "",
        ip: "",
        deviceIp: "",
    });

    const [appliedFilters, setAppliedFilters] = useState(filters);

    const [showConcludeModal, setShowConcludeModal] = useState(false);
    const [pendingCompany, setPendingCompany] = useState<IHistory | null>(null);

    const [showChangeStatusModal, setShowChangeStatusModal] = useState(false);
    const [companyToChange, setCompanyToChange] = useState<IHistory | null>(null);
    const [logs, setLogs] = useState<IStatusChangeLog[]>([]);
    const [logsFrom, setLogsFrom] = useState<string>(""); 
    const [logsTo, setLogsTo] = useState<string>("");

    const HistoryQuery = useQuery<
        { items: IHistory[]; totalCount: number },
        Error,
        { History: IHistory[]; totalPages: number }
    >({
        queryKey: ["CpfHistory", pageNumber, appliedFilters],
        queryFn: () => fetchCpfHistory(pageNumber, appliedFilters),
        select: (apiResponse: any) => {
            const items = apiResponse?.data?.data || [];
            const totalPages = apiResponse?.data?.totalPages || 1;
            return { History: items, totalPages };
        },
        // @ts-ignore
        onError: () => {
            showToast("error", "Erro ao buscar histórico de CPF.");
        },
    });

    const mutationAlterarStatus = useMutation<void, Error, { protocol: string; newStatus: number; notes?: string }>({
        mutationFn: ({ protocol, newStatus, notes }) => alterarStatusCpf(protocol, newStatus, notes),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["CpfHistory"] });
            showToast("success", "Status alterado com sucesso.");
            setShowChangeStatusModal(false);
        },
        onError: () => {
            showToast("error", "Erro ao alterar o status.");
        },
    });

    const isChangingStatus = mutationAlterarStatus.status === "pending";

    const openChangeStatusModal = (company: IHistory) => {
        setCompanyToChange(company);
        setShowChangeStatusModal(true);
    };

    const loadLogs = async (protocol?: string, from?: string, to?: string) => {
        if (!protocol) return;
        try {
            const data = await fetchCpfStatusChanges(protocol, from, to);
            setLogs(data);
        } catch {
            showToast("error", "Erro ao carregar o histórico de alterações.");
        }
    };

    useEffect(() => {
        if (showChangeStatusModal && companyToChange?.protocol) {
            loadLogs(companyToChange.protocol);
        }
    }, [showChangeStatusModal, companyToChange?.protocol]);

    const confirmAlterarStatus = (newStatus: number, notes?: string) => {
        if (!companyToChange?.protocol) return;
        mutationAlterarStatus.mutate({ protocol: companyToChange.protocol, newStatus, notes });
    };

    return {
        History: HistoryQuery.data?.History || [],
        totalPages: HistoryQuery.data?.totalPages || 1,
        isLoading: HistoryQuery.isLoading,

        search, setSearch,
        pageNumber, setPageNumber,

        filters, setFilters, setAppliedFilters,

        openChangeStatusModal,
        showChangeStatusModal,
        setShowChangeStatusModal,
        companyToChange,
        confirmAlterarStatus,
        isChangingStatus,

        logs,
        logsFrom, setLogsFrom,
        logsTo, setLogsTo,
        reloadLogs: () => loadLogs(companyToChange?.protocol, logsFrom, logsTo),
    };
}